import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { set, clone, isEqual } from 'lodash';
import { CarouselRef } from 'antd/lib/carousel';
import {
  Alert,
  Color,
  Modal,
  Button,
  Title,
  Steps,
  Text,
  Select,
  Spin,
} from '@bb/elements-web';
import { AuthContext } from 'contexts';
import { saveConfig } from 'api/AppointmentConfigController';
import { AccessType, LockBoxAccessType } from '@bb/domain/dist/types';

import { message } from 'antd';
import errorReporter from '@app/errorReporter';
import { ListingData, ShowingConfigData } from './types';
import {
  AlarmInstructions,
  ListingInfo,
  ContentSkeleton,
  TitleSkeleton,
  FooterSkeleton,
} from './components';
import {
  CombinationLockBox,
  Direct,
  DoorCode,
  Key,
  Masterlock,
  SentrilockApp,
  SentrilockV2,
  SupraEKey,
  SupraMVP,
  Empty,
} from './components/AccessOptionComponents';
import useListingLockboxTypes from './useListingLockboxTypes';

type ShowingSettingsModalProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  data: ListingConfigData | null;
  setData: React.Dispatch<React.SetStateAction<ListingConfigData | null>>;
};

type AccessOption = {
  label: string;
  value: AccessType;
};

export type ListingConfigData = {
  listings: ListingData[];
  showingConfigs: ShowingConfigData[];
};

const DEFAULT_DATA: ListingConfigData = {
  listings: [],
  showingConfigs: [],
};

const ShowingSettingsModal: React.FC<ShowingSettingsModalProps> = ({
  open,
  setOpen,
  data,
  setData,
}) => {
  const savingPromises = useRef<
    Promise<{
      listingId: string;
      showingConfigId: string;
    } | null>[]
  >([]);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isAccessOptionValid, setIsAccessOptionValid] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [closeAlarmInstructions, setCloseAlarmInstructions] = useState(false);

  const [{ listings, showingConfigs }, setListingConfig] =
    useState<ListingConfigData>(DEFAULT_DATA);

  const [currentListingIndex, setCurrentListingIndex] = useState(0);
  const carRef = useRef<CarouselRef>();
  const lockboxTypes = useListingLockboxTypes(
    open ? listings[currentListingIndex]?.id : undefined,
  );

  const { auth } = useContext(AuthContext);

  useEffect(() => {
    if (listings.length) return;

    if (open && data) {
      setCloseAlarmInstructions(false);
      setListingConfig(data);
    }
  }, [open, data, listings.length]);

  const isLastListing = currentListingIndex >= listings.length - 1;

  const unconfiguredListingsCount = showingConfigs.filter(
    (config, i) =>
      config.access === '' ||
      (i === currentListingIndex && !isAccessOptionValid),
  ).length;

  const options = useMemo<AccessOption[]>(() => {
    const combinationOption = {
      value: 'Lock Box',
      label: 'Combination Lock Box',
    };
    const showCombinationOption = lockboxTypes.combinationLockbox;

    const supraEkeyAccessOptions = { value: 'Supra eKEY', label: 'Supra eKEY' };
    const supraLockBoxAccessOptions = {
      value: 'Supra Lockbox',
      label: 'Supra',
    };

    const isSupraMVPEnabled = auth.org.features.includes('SUPRA_MVP');
    const showSupraLockBoxAccessOption =
      isSupraMVPEnabled && (!lockboxTypes || lockboxTypes.supra);
    const showSupraEkeyAccessOptions = !isSupraMVPEnabled;

    const showMasterlockOption = lockboxTypes.masterLock;
    const masterlockOption = { value: 'MasterLock', label: 'Master Lock' };

    const showSentrilockAccessOption = lockboxTypes.sentriLock;
    const sentrilockOption = { value: 'SentriLockV2', label: 'SentriLock' };

    return [
      ...(showCombinationOption ? [combinationOption] : []),
      {
        value: 'Direct',
        label: 'Direct',
      },
      {
        value: 'Key',
        label: 'Key',
      },
      {
        value: 'Door Code',
        label: 'Door Code',
      },
      ...(showSupraLockBoxAccessOption ? [supraLockBoxAccessOptions] : []),
      ...(showSupraEkeyAccessOptions ? [supraEkeyAccessOptions] : []),
      ...(showSentrilockAccessOption ? [sentrilockOption] : []),
      ...(showMasterlockOption ? [masterlockOption] : []),
    ] as AccessOption[];
  }, [lockboxTypes, auth.org]);

  const updateShowingConfigByIndex = useCallback(
    (i: number) =>
      (fn: (targetConfig: ShowingConfigData) => ShowingConfigData) => {
        const showingConfig = showingConfigs[i];
        const updatedConfig = fn(showingConfig);
        setListingConfig(prev => {
          const next = clone(prev.showingConfigs);
          return {
            ...prev,
            showingConfigs: set(next, `${i}`, updatedConfig),
          };
        });
      },
    [showingConfigs],
  );

  const resetModal = () => {
    // collapse alarm modal
    setCloseAlarmInstructions(true);

    // close main modal
    setOpen(false);

    // close confirm modal
    setIsConfirmOpen(false);

    // reset listings so next time we open it will
    // be copied from outside
    setListingConfig(DEFAULT_DATA);

    // return current listing to the beginning
    setCurrentListingIndex(0);

    // clear saved listings
    savingPromises.current = [];
  };

  const getAccessOptionComponent = (access: AccessType) => {
    switch (access) {
      case 'Lock Box':
        return CombinationLockBox;
      case 'Direct':
        return Direct;
      case 'Key':
        return Key;
      case 'Door Code':
        return DoorCode;
      case 'Supra eKEY':
        return SupraEKey;
      case 'Supra Lockbox':
        return SupraMVP;
      case 'SentriLockV2':
        return SentrilockV2;
      case 'SentriLock App':
        return SentrilockApp;
      case 'MasterLock':
        return Masterlock;
      default:
        return Empty;
    }
  };

  const validationErrorMessage = (
    ['Supra Lockbox', 'SentriLockV2', 'MasterLock'] as LockBoxAccessType[]
  ).includes(showingConfigs[currentListingIndex]?.access as LockBoxAccessType)
    ? 'Select a lockbox'
    : 'Enter access instructions';

  const checkIfConfigHasChanged = (listingIndex: number) => {
    const oldConfig = data?.showingConfigs[listingIndex];
    const newConfig = showingConfigs[listingIndex];

    return !isEqual(newConfig, oldConfig);
  };

  const saveListing = async (listingIndex: number) => {
    const listingId = listings[listingIndex].id;
    const hasChangedConfig = checkIfConfigHasChanged(listingIndex);
    const newConfig = showingConfigs[listingIndex];

    if (!isAccessOptionValid || !hasChangedConfig) return null;

    try {
      await saveConfig(
        {
          listingId,
        },
        { config: showingConfigs[listingIndex] },
      );
    } catch (err) {
      message.error(
        err?.response?.data
          ? err.response.data.message
          : 'Failed to update access instructions.',
      );
      errorReporter.error(err);

      return null;
    }
    return { listingId, showingConfigId: newConfig._id.toString() };
  };

  const awaitSavingPromises = async () => {
    setSaving(true);
    const savedObjects = (await Promise.allSettled(savingPromises.current))
      .map(result => {
        if (result.status === 'fulfilled') {
          return result.value;
        }
        return null;
      })
      .filter(value => value !== null);

    const savedListingIds = savedObjects.map(obj => obj!.listingId);
    const savedShowingConfig = savedObjects.map(obj => obj!.showingConfigId);

    setData(prev => {
      const { listings: _listings, showingConfigs: _showingConfigs } = prev!;

      return {
        listings: _listings.filter(({ id }) => !savedListingIds.includes(id)),
        showingConfigs: _showingConfigs.filter(
          ({ _id: id }) => !savedShowingConfig.includes(id.toString()),
        ),
      };
    });
    setSaving(false);
  };

  const openConfirmModal = async (listingIndex: number) => {
    const hasChangedConfig = checkIfConfigHasChanged(listingIndex);

    // if the current config has unsaved changes we show the confirm modal
    // otherwise we close main modal
    if (hasChangedConfig) {
      setOpen(false);
      setIsConfirmOpen(true);
      return;
    }

    await awaitSavingPromises();
    resetModal();
  };

  const saveAndContinue = (listingIndex: number) => {
    savingPromises.current.push(saveListing(listingIndex));
    const nextListingIndex = listingIndex + 1;
    setCurrentListingIndex(nextListingIndex);
    carRef.current?.goTo(nextListingIndex);
  };

  const saveAndExit = async (listingIndex: number) => {
    savingPromises.current.push(saveListing(listingIndex));
    await awaitSavingPromises();
    resetModal();
  };

  const loading = !listings.length || !showingConfigs.length;

  return (
    <>
      <Modal
        dataCy="showing-notification-modal"
        onCancel={() => openConfirmModal(currentListingIndex)}
        open={open}
        footer={
          loading ? (
            <FooterSkeleton />
          ) : (
            <StyledFooter>
              {showValidationError && !isAccessOptionValid && (
                <StyledAlert
                  message={<Text size="large">{validationErrorMessage}</Text>}
                  type="error"
                  showIcon
                />
              )}
              <Button
                disabled={isLastListing && saving}
                dataCy="button-next"
                type="primary"
                onClick={() => {
                  if (!isAccessOptionValid) {
                    setShowValidationError(true);
                    return;
                  }
                  setShowValidationError(false);

                  return isLastListing
                    ? saveAndExit(currentListingIndex)
                    : saveAndContinue(currentListingIndex);
                }}
              >
                {isLastListing ? 'Save' : 'Save and continue'}
              </Button>
              {listings.length > 1 && (
                <Steps
                  dataCy="showing-notification-steps"
                  steps={listings.length}
                  current={currentListingIndex}
                />
              )}
            </StyledFooter>
          )
        }
      >
        <>
          {(() => {
            const { access } = showingConfigs[currentListingIndex] ?? {};
            const AccessOptionComponent = getAccessOptionComponent(access);
            const listing = listings[currentListingIndex];

            return (
              <div key={listing?.id ?? 'skeleton'}>
                <StyledScrollableContainer>
                  {loading ? (
                    <TitleSkeleton />
                  ) : (
                    <StyledTitleWrapper>
                      <Alert
                        message={
                          <Text size="large">
                            {`${unconfiguredListingsCount} Listing${
                              unconfiguredListingsCount > 1 ? 's' : ''
                            }: Missing Access Instructions`}
                          </Text>
                        }
                        type="error"
                        style={{
                          visibility: unconfiguredListingsCount
                            ? 'visible'
                            : 'hidden',
                        }}
                      />
                      <Title dataCy="showing-notification-title" level={2}>
                        Don&apos;t miss out on showings! <br /> Add access
                        instructions.
                      </Title>
                    </StyledTitleWrapper>
                  )}

                  <StyledContent>
                    {loading ? (
                      <ContentSkeleton />
                    ) : (
                      <>
                        <ListingInfo listing={listing} loading={loading} />

                        <Select<AccessType, (typeof options)[number]>
                          label="Access type"
                          placeholder="Select access type"
                          dataCy="showing-notification-select-access-type"
                          width="100%"
                          required
                          disabled={loading}
                          value={access || undefined}
                          onChange={val => {
                            updateShowingConfigByIndex(currentListingIndex)(
                              prev => ({
                                ...prev,
                                access: val,
                              }),
                            );
                          }}
                          options={options}
                        />
                        <AccessOptionComponent
                          listing={listing}
                          showingsConfig={showingConfigs[currentListingIndex]}
                          showValidationError={showValidationError}
                          setShowingsConfig={updateShowingConfigByIndex(
                            currentListingIndex,
                          )}
                          setIsValid={setIsAccessOptionValid}
                        />
                        <AlarmInstructions
                          showingsConfig={showingConfigs[currentListingIndex]}
                          setShowingsConfig={updateShowingConfigByIndex(
                            currentListingIndex,
                          )}
                          forceClose={closeAlarmInstructions}
                        />
                      </>
                    )}
                  </StyledContent>
                </StyledScrollableContainer>
              </div>
            );
          })()}
        </>
      </Modal>

      {/* Confirmation modal */}
      <Modal
        dataCy="showing-notification-confirmation-modal"
        open={isConfirmOpen}
        title="Quit editing?"
        cancelText="Keep editing"
        onCancel={() => {
          setIsConfirmOpen(false);
          setOpen(true);
        }}
        okText="Yes, quit"
        onOk={async () => {
          await awaitSavingPromises();
          resetModal();
        }}
        width={907}
        hideCloseIcon
      >
        <Text size="large">The changes you made so far will be saved.</Text>
      </Modal>
      <Spin spinning={saving} dataCy="showing-notification-spin" fullscreen />
    </>
  );
};

const StyledTitleWrapper = styled.div`
  text-align: center;
  gap: 12px;

  > div:first-child {
    margin-bottom: 12px;
  }
`;

const StyledContent = styled.div`
  max-width: 372px;
  min-height: 200px;
  margin: auto;
  display: flex;
  gap: 32px;
  flex-direction: column;
`;

const StyledScrollableContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-content: start;
  margin-top: 32px;
  padding: 5px;
  gap: 32px;

  height: calc(100vh - 530px);
  overflow-y: scroll;
  min-height: 400px;

  ::-webkit-scrollbar {
    width: 0.5em;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 5px;
    background-color: darkgrey;
  }
`;

const StyledFooter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: auto;
  gap: 15px;

  border-top: 1px solid ${Color.LIGHTER_GREY};
  padding-top: 15px;
  padding-bottom: 15px;

  > button {
    width: 300px;
  }
`;

const StyledAlert = styled(Alert)`
  display: flex;
  justify-content: center;
  width: 300px;

  > .ant5-alert-content {
    flex: none;
  }
`;

export default ShowingSettingsModal;
